import { useCallback, useMemo } from "react";

import {
  copy,
  AssetType,
  Item,
  ItemType,
  pluralize,
  slugifyName,
  buildPath,
  buildURL,
  useNavigation,
  SpacePermission,
} from "@thenounproject/lingo-core";

import useBatchUpdateAssetsMetadata from "@redux/actions/assets/useBatchUpdateAssetsMetadata";
import useShowModal, { ModalTypes } from "@redux/actions/useModals";
import useSetKitCoverWithAsset from "@redux/actions/kits/useUpdateKitCoverWithAsset";
import useNotifications from "@actions/useNotifications";
import useBatchSaveItems from "@redux/actions/items/useBatchSaveItems";
import { Inspectable } from "@constants/Inspector";

import { useGetSections } from "@selectors/getters";
import { buildLibraryAssetUrl } from "@features/library/utils";
import useDeleteAssets from "@redux/actions/assets/useDeleteAssets";
import useDeleteAssetItems from "@redux/actions/assets/useDeleteAssetItems";
import { NavPointTypes } from "@redux/navPoint/NavPoint";
import useDeleteTags from "@redux/actions/tags/useDeleteTags";
import useUpdateTags from "@redux/actions/tags/useUpdateTags";
import {
  AssetCopyTypes,
  SectionCallbackArgs,
} from "@features/content-management/modals/MoveCopyToSectionModal";
import useMoveItems from "@redux/actions/items/useMoveItems";
import useDuplicateItems from "@redux/actions/items/useDuplicateItems";
import useRemoveAssetThumbnail from "@redux/actions/assets/useRemoveAssetThumbnail";
import useBatchUpdateItemStatus from "@redux/actions/items/useBatchUpdateItemStatus";
import { useSelectedItemsContext } from "@contexts/SelectedItemsProvider";
import useNavPoint from "@hooks/useNavPoint";
import useContentTypes from "@redux/actions/useFetchContentTypes";

function canChangeBackgroundColor(inspectable: Inspectable) {
  return (
    (!inspectable?.item || inspectable.item.type === ItemType.asset) &&
    ![AssetType.color, AssetType.textStyle].includes(inspectable.asset?.type)
  );
}

function canChangeDisplayStyle(item: Item) {
  return item.type === ItemType.asset;
}

export default function useInspectableActions(inspectables: Inspectable[]) {
  const navPoint = useNavPoint(),
    { space, portal } = navPoint || {},
    navigate = useNavigation(),
    [batchUpdateAssetsMetadata] = useBatchUpdateAssetsMetadata(),
    [setKitCoverWithAsset] = useSetKitCoverWithAsset(),
    { showNotification } = useNotifications(),
    { showModal } = useShowModal(),
    [_deleteTags] = useDeleteTags(),
    [_editTag] = useUpdateTags(),
    [batchSaveItems] = useBatchSaveItems(),
    [moveItems] = useMoveItems(),
    [duplicateItems] = useDuplicateItems(),
    [removeAssetThumbnail] = useRemoveAssetThumbnail(),
    [batchUpdateItemStatus] = useBatchUpdateItemStatus();

  const { deselectItems } = useSelectedItemsContext();

  const sections = useGetSections();

  const singleItem = inspectables.length === 1 ? inspectables[0] : null;

  const { data: contentTypes } = useContentTypes(
    {
      assetType: singleItem?.asset?.type,
    },
    { skip: !singleItem?.asset?.type }
  );

  const includesGalleryItems = useMemo(
    () => inspectables.some(i => i.item?.itemUuid),
    [inspectables]
  );

  // Check the nav point to see if we are inside a manual gallery
  const currentItem = navPoint?.item;
  const inspectablesInsideManualGallery = useMemo(() => {
    return !!(currentItem?.type === ItemType.gallery && !currentItem?.data?.viewId);
  }, [currentItem]);

  // MARK : Item Actions
  // -------------------------------------------------------------------------------

  const canRestoreItems = useMemo(
      () => !includesGalleryItems && inspectables.some(i => i.item?.status === "trashed"),
      [includesGalleryItems, inspectables]
    ),
    restoreItems = useCallback(() => {
      if (!canRestoreItems) return;
      const itemIds = inspectables.filter(i => i.item?.status === "trashed").map(i => i.item?.uuid);
      void batchUpdateItemStatus({ items: itemIds, status: "active" });
    }, [batchUpdateItemStatus, canRestoreItems, inspectables]);

  // We can trash if all items are active asset type & not a recovered asset
  const canTrashItems = useMemo(
      () =>
        (!includesGalleryItems || inspectablesInsideManualGallery) &&
        inspectables.every(
          i =>
            i.item?.type === ItemType.asset &&
            i.item?.status === "active" &&
            (i.item.sectionUuid || i.item.itemUuid)
        ),
      [includesGalleryItems, inspectables, inspectablesInsideManualGallery]
    ),
    trashItems = useCallback(() => {
      if (!canTrashItems) return;
      const itemIds = inspectables
        .filter(
          i =>
            i.item?.type === ItemType.asset && i.item?.status === "active" && !i.item.data?.viewId
        )
        .map(i => i.item?.uuid);
      void batchUpdateItemStatus({ items: itemIds, status: "trashed" });
    }, [batchUpdateItemStatus, canTrashItems, inspectables]);

  // We can delete if all items are trashed OR type is not asset
  const canDeleteItems = useMemo(
      () =>
        !includesGalleryItems &&
        inspectables.every(
          i => i.item && (i.item?.status === "trashed" || i.item?.type !== ItemType.asset)
        ),
      [includesGalleryItems, inspectables]
    ),
    deleteItems = useCallback(() => {
      if (!canDeleteItems) return;
      const itemIds = inspectables.map(i => i.item?.uuid).filter(Boolean);
      void batchUpdateItemStatus({ items: itemIds, status: "deleted" });
    }, [batchUpdateItemStatus, canDeleteItems, inspectables]);

  // We can move active items of any type
  const canMoveItems = useMemo(
      () => !includesGalleryItems && inspectables.some(i => i.item?.status === "active"),
      [includesGalleryItems, inspectables]
    ),
    moveItemsCallback = useCallback(
      async ({ fromSectionId, selectedSection }: SectionCallbackArgs) => {
        const itemIds = inspectables.map(i => i.item?.uuid).filter(Boolean);
        await moveItems({
          itemIds,
          fromSectionId,
          toSectionId: selectedSection.uuid,
          ctx: { space, portal },
        });
      },
      [inspectables, moveItems, portal, space]
    ),
    onMoveItems = useCallback(() => {
      if (!canMoveItems) return;
      const itemIds = inspectables.map(i => i.item?.uuid).filter(Boolean);
      showModal(ModalTypes.MOVE_COPY_TO_SECTION, {
        type: "move",
        fromSectionId: inspectables[0].item?.sectionUuid,
        kitId: inspectables[0].item?.kitUuid,
        callback: moveItemsCallback,
        entityText: `${itemIds.length} ${pluralize("asset", itemIds.length)}`,
      });
    }, [canMoveItems, inspectables, moveItemsCallback, showModal]),
    copyItemsCallback = useCallback(
      ({ selectedSection, copyAs }: SectionCallbackArgs) => {
        const itemIds = inspectables.map(i => i.item?.uuid).filter(Boolean);
        void duplicateItems({
          itemIds,
          toSectionId: selectedSection.uuid,
          createReferences: copyAs === AssetCopyTypes.reference,
        });
      },
      [duplicateItems, inspectables]
    ),
    onCopyItems = useCallback(() => {
      if (!canMoveItems) return;
      const itemIds = inspectables.map(i => i.item?.uuid).filter(Boolean);
      showModal(ModalTypes.MOVE_COPY_TO_SECTION, {
        type: "copy",
        fromSectionId: inspectables[0].item?.sectionUuid,
        callback: copyItemsCallback,
        entityText: `${itemIds.length} ${pluralize("asset", itemIds.length)}`,
      });
    }, [canMoveItems, copyItemsCallback, inspectables, showModal]);

  //Rename/configure gallery item
  const canRenameGalleryItem = useMemo(() => {
    return (
      singleItem &&
      inspectables.every(i => i.item?.status === "active" && i.item?.type === ItemType.gallery)
    );
  }, [inspectables, singleItem]);
  const renameGalleryItem = useCallback(
    galleryType => {
      if (!canRenameGalleryItem) return;
      showModal(ModalTypes.CREATE_GALLERY_ITEM, {
        item: inspectables[0].item,
        defaultGalleryType: galleryType,
      });
    },
    [canRenameGalleryItem, inspectables, showModal]
  );

  // Edit Asset Background
  const canEditAssetBackgroundColor = useMemo(() => {
    return inspectables.every(i => canChangeBackgroundColor(i));
  }, [inspectables]);
  const editAssetBackground = useCallback(
    (background: string) => {
      if (!canEditAssetBackgroundColor) return;
      const updates = inspectables
        .filter(inspectable => inspectable.asset)
        .map(inspectable => ({
          uuid: inspectable.asset.uuid,
          meta: { background_color: background },
        }));
      void batchUpdateAssetsMetadata(updates);
    },
    [canEditAssetBackgroundColor, inspectables, batchUpdateAssetsMetadata]
  );

  //Edit Item Background
  const canEditItemBackgroundColor = useMemo(() => {
      const editableBackgroundTypes = [ItemType.note, ItemType.gallery];
      return inspectables.every(
        i =>
          i.item?.status === "active" &&
          editableBackgroundTypes.includes(i.item?.type) &&
          i.item?.type === inspectables[0].item?.type
      );
    }, [inspectables]),
    editItemBackground = useCallback(
      (background: string, itemType: ItemType) => {
        if (!canEditItemBackgroundColor) return;

        const updates = inspectables
          .filter(i => i.item && i.item?.type === itemType)
          .map(i => ({
            uuid: i.item.uuid,
            data: { background_color: background },
          }));
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        batchSaveItems({ updatedItems: updates });
      },
      [batchSaveItems, canEditItemBackgroundColor, inspectables]
    );

  // Edit display style
  const canEditDisplayStyle = useMemo(
      () =>
        !includesGalleryItems &&
        inspectables.some(i => i.item?.status === "active" && i.item?.type === ItemType.asset),
      [includesGalleryItems, inspectables]
    ),
    _editDisplayStyle = useCallback(
      (style: Item["data"]["displayStyle"]) => {
        if (!canEditDisplayStyle) return;
        const updates = inspectables
          .filter(i => i.item && canChangeDisplayStyle(i.item))
          .map(i => ({
            uuid: i.item.uuid,
            data: {
              display_style: style,
            },
          }));
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        batchSaveItems({ updatedItems: updates });
      },
      [batchSaveItems, canEditDisplayStyle, inspectables]
    ),
    editDisplayStyle = {
      row: useCallback(() => _editDisplayStyle("full_width"), [_editDisplayStyle]),
      square: useCallback(() => _editDisplayStyle("square"), [_editDisplayStyle]),
    };

  // Kit Cover
  const canSetKitCover = useMemo(() => {
      if (singleItem?.item?.status !== "active") return false;
      if (singleItem?.item?.type !== ItemType.asset) return false;
      const allowedTypes = [
        AssetType.png,
        AssetType.jpg,
        AssetType.gif,
        AssetType.svg,
        AssetType.pdf,
        AssetType.eps,
        AssetType.tiff,
        ...AssetType.sketchTypes,
      ];
      return allowedTypes.includes(singleItem?.asset?.type ?? "");
    }, [singleItem]),
    setAsKitCover = useCallback(async () => {
      if (!canSetKitCover) return;
      const { item } = singleItem;
      await setKitCoverWithAsset({
        kitId: item.kitUuid,
        spaceId: item.spaceId,
        assetId: item.asset.uuid,
      });
    }, [canSetKitCover, setKitCoverWithAsset, singleItem]);

  // MARK : Library actions
  // -------------------------------------------------------------------------------
  const isLibrarySelection = useMemo(() => !inspectables.some(i => i.item), [inspectables]);
  const canAddToKit = isLibrarySelection;
  const addToKit = useCallback(() => {
    const assetIds = inspectables.map(i => i.asset.uuid);
    showModal(ModalTypes.ADD_ASSETS_TO_KIT, { assetIds });
  }, [inspectables, showModal]);

  const canDeleteAllAssetItems = isLibrarySelection;
  const [_deleteAssetItems] = useDeleteAssetItems();
  const deleteAllAssetItems = useCallback(() => {
    if (!canDeleteAllAssetItems) return;
    showModal(ModalTypes.CONFIRMATION, {
      title: `Remove asset from all kits?`,
      message: "Asset will remain in your library but removed from all kits.",
      buttonText: "Remove",
      buttonProcessingText: "Removing...",
      onConfirm: () => {
        const assetIds = inspectables.map(i => i.asset.uuid);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        _deleteAssetItems({ assetIds }).then(action => {
          if (!action.error) {
            const items = Object.values(action.response.entities.items ?? {}) as Item[];
            const numKits = items.reduce((acc, item) => {
              acc.add(item.kitUuid);
              return acc;
            }, new Set()).size;
            if (!numKits) {
              return showNotification({
                message: `The selected ${pluralize(
                  "asset",
                  assetIds.length
                )} has no instances to remove`,
              });
            }
            showNotification({
              message: `Removed ${pluralize("asset", assetIds.length)} from ${numKits} ${pluralize(
                "kit",
                numKits
              )}`,
            });
          } else showNotification({ message: action.error.message, level: "error" });
        });
      },
    });
  }, [_deleteAssetItems, canDeleteAllAssetItems, inspectables, showModal, showNotification]);

  // Delete Assets
  const canDeleteAssets =
    space.access.permissions.includes(SpacePermission.viewAssetLibrary) &&
    inspectables.some(i => (i.item?.version === 0 && i.item?.type === ItemType.asset) || !i.item);
  const [_deleteAssets] = useDeleteAssets();
  const deleteAssets = useCallback(() => {
    if (!canDeleteAssets) return;
    const assetsToDelete = inspectables
      .filter(i => (i.item?.version === 0 && i.item?.type === ItemType.asset) || !i.item)
      .map(i => i.asset || i.item?.asset);
    const numAssets = assetsToDelete.length;
    showModal(ModalTypes.CONFIRMATION, {
      title: `Delete ${numAssets} ${pluralize("asset", numAssets)}?`,
      message: `${pluralize(
        "Asset",
        numAssets
      )} will be removed from all your kits and deleted from your space.`,
      buttonText: "Delete",
      buttonProcessingText: "Deleting...",
      onConfirm: async () => {
        const assetIds = assetsToDelete.map(a => a.uuid);

        const { response: { result } = {}, error } = await _deleteAssets({ assetIds });
        if (error) {
          showNotification({ message: error.message, level: "error" });
        } else {
          if (navPoint.type === NavPointTypes.LibraryAsset) {
            navigate.push(buildPath("/library", { space }));
          }
          const errors = result.assets.filter(f => f.error);
          const succeeded = assetIds.length - errors.length;
          if (succeeded) {
            const remainingItems = inspectables
              .filter(i => !assetIds.includes(i.item?.asset?.uuid))
              .map(i => i.item?.uuid)
              .filter(Boolean);
            if (remainingItems.length > 0) {
              void batchUpdateItemStatus({ items: remainingItems, status: "deleted" });
            }
            showNotification({
              message: `${succeeded} ${pluralize("asset", succeeded)} deleted`,
            });
          }

          if (errors.length) {
            errors.forEach(err => {
              showNotification({
                message: err?.error?.message || "Failed to delete asset",
                level: "error",
              });
            });
          }

          deselectItems({ type: "assetLibrary", ids: assetIds });
        }
      },
    });
  }, [
    canDeleteAssets,
    inspectables,
    showModal,
    _deleteAssets,
    showNotification,
    navPoint.type,
    deselectItems,
    navigate,
    space,
    batchUpdateItemStatus,
  ]);

  // MARK : Library OR Kit actions
  // -------------------------------------------------------------------------------

  // Edit Color
  const canEditColor = useMemo(() => {
      if (singleItem?.item && singleItem?.item?.status !== "active") return false;
      return singleItem?.asset?.type === AssetType.color;
    }, [singleItem]),
    editColor = useCallback(() => {
      if (!canEditColor) return;
      showModal(ModalTypes.CREATE_EDIT_COLOR, {
        asset: singleItem.asset,
      });
    }, [canEditColor, showModal, singleItem?.asset]);

  // Edit Thumbnails
  const canEditThumbnail = useMemo(() => {
      // if asset already has a preview, we will render 'remove thumbnail' instead
      if (singleItem?.asset?.meta?.preview?.id) return false;

      const isEligibleForCustomPreviews = contentTypes?.[0]?.eligibleForCustomPreviews;

      return singleItem?.item?.type === ItemType.asset && isEligibleForCustomPreviews;
    }, [singleItem, contentTypes]),
    editThumbnail = useCallback(() => {
      if (!canEditThumbnail) return;
      if (AssetType.motionTypes.has(inspectables[0].asset?.type)) {
        showModal(ModalTypes.EDIT_MOTION_ASSET_THUMBNAIL, {
          asset: inspectables[0].asset,
        });
      } else {
        showModal(ModalTypes.EDIT_ASSET_THUMBNAIL, {
          asset: inspectables[0].asset,
        });
      }
    }, [canEditThumbnail, inspectables, showModal]);

  // Remove Thumbnail
  const canRemoveThumbnail = useMemo(() => {
    const hasThumbnail = singleItem?.asset?.meta?.preview?.id;
    const isEligibleForCustomPreviews = contentTypes?.[0]?.eligibleForCustomPreviews;

    return hasThumbnail && isEligibleForCustomPreviews && singleItem?.item?.type === ItemType.asset;
  }, [singleItem, contentTypes]);

  const removeThumbnail = useCallback(() => {
    if (!canRemoveThumbnail) return;
    void removeAssetThumbnail({ assetId: singleItem.asset.uuid });
  }, [canRemoveThumbnail, removeAssetThumbnail, singleItem]);

  const canGetShareLink = singleItem?.item || singleItem?.asset,
    getShareLink = useCallback(() => {
      const { item, asset } = singleItem;

      if (item) {
        let path = "",
          hash = "",
          assetToken = undefined;
        switch (item.type) {
          case ItemType.asset:
            // Link to asset detail
            path = ["a", slugifyName(item.asset?.name, item.shortId)].join("/");
            assetToken = item.asset?.shareToken;
            break;
          case ItemType.gallery: {
            //Link to gallery
            path = `a/${item.urlId}`;
            break;
          }
          default: {
            // Link to item in the section
            let sectionId = item.sectionUuid;
            const section = sections[`${sectionId}-${item.version}`];
            if (section?.shortId) {
              sectionId = slugifyName(section.name, section.shortId);
            }
            path = `/s/${sectionId}`;
            hash = item.shortId;
          }
        }
        return buildURL(
          path,
          { space, portal, isPublic: true },
          { v: item.version, asset_token: assetToken },
          hash
        );
      } else if (asset) {
        // Library link
        return buildLibraryAssetUrl(asset, space);
      }
    }, [portal, sections, singleItem, space]),
    canCopyShareLink = canGetShareLink,
    copyShareLink = useCallback(() => {
      const url = getShareLink();
      if (url) copy(url);
    }, [getShareLink]);

  const canDeleteTags = useMemo(() => {
    // some inspectables have tags
    return inspectables.some(i => Boolean(i.tag));
  }, [inspectables]);

  const deleteTags = useCallback(() => {
    showModal(ModalTypes.CONFIRMATION, {
      title: `Delete ${pluralize("tag", inspectables.length)}?`,
      message: `The selected ${pluralize(
        "tag",
        inspectables.length
      )} will be removed from all assets and deleted from your space.`,
      buttonText: "Delete",
      buttonProcessingText: "Deleting...",
      successNotification: { message: `${pluralize("Tag", inspectables.length)} deleted` },
      onConfirm: async () => {
        const result = await _deleteTags({
          spaceId: space?.id,
          keywords: inspectables.map(i => i.tag.value),
        });
        if (result.error) {
          showNotification({
            message: result?.error?.message,
            level: "error",
          });
        } else {
          deselectItems({
            type: "tagLibrary",
            ids: inspectables.map(i => i.tag.value),
          });
        }
        return result;
      },
    });
  }, [_deleteTags, deselectItems, inspectables, showModal, showNotification, space?.id]);

  const canEditTag = useMemo(() => {
    // some inspectables have tags
    return Boolean(singleItem?.tag);
  }, [singleItem]);

  const editTag = useCallback(
    (new_keyword: string) => {
      return _editTag({
        spaceId: space?.id,
        keywords: [{ keyword: singleItem.tag.value, new_keyword }],
      });
    },
    [_editTag, singleItem, space?.id]
  );

  return {
    restoreItems: canRestoreItems ? restoreItems : null,
    moveItems: canMoveItems ? onMoveItems : null,
    copyItems: canMoveItems ? onCopyItems : null,
    trashItems: canTrashItems ? trashItems : null,
    deleteItems: canDeleteItems ? deleteItems : null,

    removeThumbnail: canRemoveThumbnail ? removeThumbnail : null,
    editColor: canEditColor ? editColor : null,

    getShareLink: canGetShareLink ? getShareLink : null,
    copyShareLink: canCopyShareLink ? copyShareLink : null,
    editThumbnail: canEditThumbnail ? editThumbnail : null,
    setAsKitCover: canSetKitCover ? setAsKitCover : null,

    renameGalleryItem: canRenameGalleryItem ? renameGalleryItem : null,
    editAssetBackground: canEditAssetBackgroundColor ? editAssetBackground : null,
    editItemBackground: canEditItemBackgroundColor ? editItemBackground : null,

    editDisplayStyle: canEditDisplayStyle ? editDisplayStyle : null,

    // Library
    removeFromKits: canDeleteAllAssetItems ? deleteAllAssetItems : null,
    deleteAssets: canDeleteAssets ? deleteAssets : null,
    addToKit: canAddToKit ? addToKit : null,
    deleteTags: canDeleteTags ? deleteTags : null,
    editTag: canEditTag ? editTag : null,
  };
}
